import { useState, useCallback } from 'react';
import { QueryKey } from 'react-query';

import { ID } from '../../../../../../../types';
import {
  UniversalFilterTagIdValue,
  UniversalFilterTagIdModes,
  UniversalFilterTagIdFilterState,
  UniversalFilterTagIdOnFilterStateChange,
  UniversalFilterTagIdOnChange
} from '../../UniversalFilterTagId.types';

import { useMountEffect } from '../../../../../../../common/hooks/useMountEffect';
import { useReactQueryState } from '../../../../../../../main/common/reactQuery/hooks/useReactQueryState';

import { useUniversalFilterTagIdFilterState } from '../useUniversalFilterTagIdFilterState';

import {
  UniversalFilterTemplateModeOption,
  UniversalFilterTemplateOnModeChange
} from '../../../../helpers/UniversalFilterTemplate';

import { filtersKeys } from '../../../../../../../locales/keys';

const defaultMode = UniversalFilterTagIdModes.CONTAINS_ANY_OF;

const modeOptions: UniversalFilterTemplateModeOption<UniversalFilterTagIdModes>[] =
  [
    {
      value: UniversalFilterTagIdModes.CONTAINS_ANY_OF,
      i18nText: filtersKeys.modes.containsAnyOf
    }
  ];

interface UniversalFilterTagIdOptions {
  filterStateCacheKey: QueryKey;
  filterValue?: UniversalFilterTagIdValue;
  onChange: UniversalFilterTagIdOnChange;
}

function useUniversalFilterTagId({
  filterStateCacheKey,
  filterValue,
  onChange
}: UniversalFilterTagIdOptions) {
  const { value: savedState, setValue: saveState } =
    useReactQueryState<UniversalFilterTagIdFilterState>(filterStateCacheKey);

  const { handleApplyFilterState, handleParseFilterValue } =
    useUniversalFilterTagIdFilterState({
      onChange
    });

  const [filterState, setFilterState] =
    useState<UniversalFilterTagIdFilterState>(
      savedState ||
        handleParseFilterValue(filterValue) || {
          mode: defaultMode,
          tagIds: []
        }
    );

  useMountEffect(() => {
    handleApplyFilterState(filterState);
  });

  const handleFilterStateChange =
    useCallback<UniversalFilterTagIdOnFilterStateChange>(
      (state) => {
        setFilterState(state);
        handleApplyFilterState(state);
        saveState(state);
      },
      [handleApplyFilterState, saveState]
    );

  const handleModeChange = useCallback<
    UniversalFilterTemplateOnModeChange<UniversalFilterTagIdModes>
  >(
    (mode) => {
      handleFilterStateChange({
        mode,
        tagIds: filterState.tagIds || []
      });
    },
    [filterState, handleFilterStateChange]
  );

  const handleTagIdsChange = useCallback<(tagIds: ID[]) => void>(
    (tagIds) => {
      handleFilterStateChange({
        mode: filterState.mode,
        tagIds: tagIds || []
      });
    },
    [filterState, handleFilterStateChange]
  );

  return {
    modeOptions,
    filterState,
    handleModeChange,
    handleTagIdsChange
  };
}

export default useUniversalFilterTagId;
